import React, { PropsWithChildren } from 'react'

type State = {
    hasError: boolean
    error: Error | null
}

const RELOAD_COOLDOWN_MS = 10_000

export class ErrorBoundary extends React.Component<PropsWithChildren<{}>, State> {
    constructor(props: PropsWithChildren<{}>) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        console.error('[ErrorBoundary] Caught error:', error, info)

        const isChunkLoadError =
            error.name === 'ChunkLoadError' ||
            /Loading chunk [\d]+ failed/.test(error.message) ||
            // React #306: a React.lazy() import resolved to undefined, typically because a stale/mismatched
            // webpack module registry returned no module for the requested chunk.
            /Minified React error #306/.test(error.message) ||
            // webpack's __webpack_require__ found no module for the requested id in its module map
            // (message wording differs by browser: Chrome says "reading 'call'", Firefox says
            // 'access property "call"').
            (error.name === 'TypeError' && /reading 'call'|access property "call"/.test(error.message))
        if (!isChunkLoadError) return

        // window.location.reload() is a hard navigation, so it tears the page down before
        // componentWillUnmount can run - a one-shot sessionStorage flag would never get cleared
        // and would permanently block retries for this route. A time-based cooldown survives
        // reloads correctly while still preventing infinite reload loops on a persistently broken route.
        const key = `chunk-reload:${window.location.hash.split('?')[0]}`
        const lastReloadAt = Number(sessionStorage.getItem(key)) || 0
        if (Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS) {
            sessionStorage.setItem(key, String(Date.now()))
            window.location.reload()
        }
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#666'
                    }}
                >
                    Something went wrong loading this page.
                </div>
            )
        }

        return this.props.children
    }
}
